"use client"

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { Check, FolderPlus, Loader2, TriangleAlert, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAuth } from "@/hooks/useAuth"
import { getBiblioChunkContext } from "@/services/biblioFulltextService"
import {
  addSourcesToCollection,
  isCollectionMember,
  USER_COLLECTION_TYPE,
  type BiblioCollection,
} from "@/services/biblioService"
import {
  Popover,
  PopoverContent,
  PopoverTitle,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { useCollection } from "./CollectionContext"
import type {
  DeepLiteratureEvidenceChunk,
  DeepLiteratureEvidenceMarker,
  SourceMetadata,
} from "./deepLiteratureReferences"

type DeepLiteratureEvidencePopoverProps = {
  marker: DeepLiteratureEvidenceMarker
  chunk?: DeepLiteratureEvidenceChunk
  sourceMetadata?: SourceMetadata
  children: ReactNode
}

/** Props for the {@link AddSourceToCollectionButton}. */
interface AddSourceToCollectionButtonProps {
  /**
   * The biblio source id to add to a collection. Markers carry this as a
   * string (e.g. "894"); the backend expects a numeric id, so it is parsed on
   * add.
   */
  sourceId: string
}

/**
 * The per-collection add state for a single source. `idle` is the default,
 * `adding` while the link request is in flight, `added` once it has succeeded
 * this session, and `error` when the add failed.
 */
type AddState = "idle" | "adding" | "added" | "error"

/**
 * Determine whether a collection already contains the given source.
 *
 * @param collection - The collection to inspect.
 * @param sourceId - The source id (string form from the marker) to look for.
 * @returns True when the source is already a member of the collection.
 */
function collectionContainsSource(
  collection: BiblioCollection,
  sourceId: string
): boolean {
  const targetNumeric: number = Number.parseInt(sourceId, 10)
  const targetHasNumeric: boolean = Number.isInteger(targetNumeric)
  const targetString: string = sourceId.trim()

  return (collection.sourceIds ?? []).some((storedId): boolean => {
    const storedString: string = String(storedId).trim()
    const storedNumeric: number = Number.parseInt(storedString, 10)
    // Prefer a numeric match when both ids are clean integers; otherwise fall
    // back to an exact trimmed string match.
    if (targetHasNumeric && Number.isInteger(storedNumeric)) {
      return storedNumeric === targetNumeric
    }
    return storedString === targetString
  })
}

/**
 * A button + dropdown that lets the user add the currently viewed source to one
 * of their own biblio collections.
 * Co-located with its only consumer, {@link DeepLiteratureEvidencePopover}.
 *
 * @param props - Component props.
 * @param props.sourceId - The biblio source id to add (string form from the marker).
 * @returns The rendered add-to-collection button with its dropdown.
 */
function AddSourceToCollectionButton({
  sourceId,
}: AddSourceToCollectionButtonProps): React.JSX.Element {
  const { getAccessToken, user } = useAuth()
  const { collections, refreshCollections } = useCollection()

  /** Whether the dropdown is open. */
  const [open, setOpen] = useState<boolean>(false)
  /** Id of the collection currently being added to (shows a spinner on its row). */
  const [addingCollectionId, setAddingCollectionId] = useState<number | null>(null)
  /** Collection ids successfully added to during this session (shown with a check). */
  const [addedCollectionIds, setAddedCollectionIds] = useState<Set<number>>(new Set())
  /** The error message from the most recent failed add, or null. */
  const [addError, setAddError] = useState<string | null>(null)

  // The numeric source id the backend expects. Parsed once; if the marker's id
  // is not a valid integer we disable the whole control rather than sending a
  // bad request.
  const numericSourceId: number = useMemo(
    () => Number.parseInt(sourceId, 10),
    [sourceId]
  )
  const sourceIdIsValid: boolean = Number.isInteger(numericSourceId)

  // Only offer the user's own collections: user-created collections of which
  // they are an explicit member.
  const addableCollections: BiblioCollection[] = useMemo(
    () =>
      collections.filter(
        (c) =>
          c.collectionType === USER_COLLECTION_TYPE &&
          isCollectionMember(c, user?.sub)
      ),
    [collections, user?.sub]
  )

  /**
   * Add this source to the given collection, then refresh the shared
   * collections list so the panel reflects the new membership.
   *
   * @param collection - The collection to link this source to.
   * @returns A promise that resolves once the add attempt (and refresh) completes.
   */
  async function handleAddToCollection(
    collection: BiblioCollection
  ): Promise<void> {
    // Ignore while another add is in flight or if the id is unusable.
    if (addingCollectionId !== null || !sourceIdIsValid) {
      return
    }

    setAddingCollectionId(collection.id)
    setAddError(null)
    try {
      const token: string = await getAccessToken()
      const response = await addSourcesToCollection({
        idToken: token,
        collectionId: collection.id,
        sourceIds: [numericSourceId],
      })

      // The add endpoint returns 200 even when a requested source id does not
      // exist in the backend: it reports the id under `sourceIdsNotFound` and
      // omits it from `sourceIdsAdded.
      const notFound: boolean = (response.sourceIdsNotFound ?? []).some(
        (id) => id === numericSourceId
      )
      if (notFound) {
        setAddError(
          `Source ${sourceId} could not be found in the library, so it was not ` +
            `added. It may not be a stored source yet.`
        )
        // Refresh so the panel does not keep showing a stale/phantom count from
        // the one-sided link the backend may have written to the collection.
        await refreshCollections()
        return
      }

      setAddedCollectionIds((prev) => {
        const next = new Set(prev)
        next.add(collection.id)
        return next
      })

      // Reflect the new source count in the Collections panel right away.
      await refreshCollections()
    } catch (err) {
      const message: string =
        err instanceof Error ? err.message : "Failed to add source to collection."
      setAddError(message)
    } finally {
      setAddingCollectionId(null)
    }
  }

  /**
   * Resolve the display state for a single collection row: whether the source
   * is already a member, was just added, is being added, or can be added.
   *
   * @param collection - The collection whose row state is resolved.
   * @returns The {@link AddState} plus whether the source already belongs.
   */
  function rowState(collection: BiblioCollection): {
    state: AddState
    alreadyContains: boolean
  } {
    const alreadyContains: boolean = collectionContainsSource(collection, sourceId)
    if (addingCollectionId === collection.id) {
      return { state: "adding", alreadyContains }
    }
    if (addedCollectionIds.has(collection.id)) {
      return { state: "added", alreadyContains }
    }
    return { state: "idle", alreadyContains }
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 gap-1.5 px-2 text-xs"
          aria-label={`Add source ${sourceId} to a collection`}
          title="Add this source to a collection"
          disabled={!sourceIdIsValid}
        >
          <FolderPlus className="size-3.5" />
          Add to collection
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
          Add source {sourceId} to collection
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {addError !== null && (
          <div
            role="alert"
            className="flex items-start gap-1.5 px-2 py-1.5 text-xs text-destructive"
          >
            <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
            <span>{addError}</span>
          </div>
        )}

        {/* Scrollable list of the user's own collections. */}
        <div className="max-h-64 overflow-y-auto">
          {addableCollections.length === 0 ? (
            <p className="px-2 py-3 text-sm text-muted-foreground">
              You have no collections to add to.
            </p>
          ) : (
            addableCollections.map((collection) => {
              const { state, alreadyContains } = rowState(collection)
              const isDone: boolean = state === "added" || alreadyContains
              const isAdding: boolean = state === "adding"
              const sourceCount: number = collection.sourceIds?.length ?? 0

              return (
                <DropdownMenuItem
                  key={collection.id}
                  // Keep the menu open after a click so the user can add to
                  // several collections in one pass.
                  onSelect={(event) => {
                    event.preventDefault()
                    if (!isDone && !isAdding) {
                      void handleAddToCollection(collection)
                    }
                  }}
                  disabled={isDone || isAdding}
                  className="flex items-start gap-2"
                >
                  <span className="mt-0.5 shrink-0">
                    {isAdding ? (
                      <Loader2 className="size-3.5 animate-spin" />
                    ) : isDone ? (
                      <Check className="size-3.5 text-green-600 dark:text-green-500" />
                    ) : (
                      <FolderPlus className="size-3.5 text-muted-foreground" />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span
                      className={cn(
                        "block truncate text-sm",
                        collection.collectionName
                          ? "text-foreground"
                          : "italic text-muted-foreground"
                      )}
                    >
                      {collection.collectionName || "(unnamed collection)"}
                    </span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">
                      {alreadyContains
                        ? "Already added"
                        : state === "added"
                          ? "Added"
                          : `${sourceCount} source${sourceCount === 1 ? "" : "s"}`}
                    </span>
                  </span>
                </DropdownMenuItem>
              )
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

type ChunkLoadState = "idle" | "loading" | "loaded" | "error"

function markerDetail(marker: DeepLiteratureEvidenceMarker): string {
  if (marker.kind === "chunk") {
    return `Source ${marker.sourceId} - p. ${marker.page} - chunk ${marker.chunkId}`
  }
  return (
    `Source ${marker.sourceId} - p. ${marker.page} - ${marker.tableId} ` +
    `r${marker.rowIndex} c${marker.columnIndex}`
  )
}

function titleForEvidence(
  chunk: DeepLiteratureEvidenceChunk | undefined,
  sourceMetadata: SourceMetadata | undefined,
  sourceId: string
): string {
  if (sourceMetadata?.citation) return sourceMetadata.citation
  if (chunk?.citation) return chunk.citation

  const title = sourceMetadata?.title ?? chunk?.title
  const publicationYear = sourceMetadata?.publicationYear ?? chunk?.publicationYear
  if (title && publicationYear) return `${title.replace(/\.$/, "")}. ${publicationYear}.`
  if (title) return title

  return `Source ${sourceId}`
}

export function DeepLiteratureEvidencePopover({
  marker,
  chunk,
  sourceMetadata,
  children,
}: DeepLiteratureEvidencePopoverProps) {
  const [open, setOpen] = useState(false)
  const [fetchedChunk, setFetchedChunk] = useState<DeepLiteratureEvidenceChunk>()
  const [loadState, setLoadState] = useState<ChunkLoadState>("idle")
  const [loadError, setLoadError] = useState<string>()
  const { getAccessToken } = useAuth()
  const getAccessTokenRef = useRef(getAccessToken)
  const requestedChunkKeyRef = useRef<string | undefined>(undefined)
  const displayChunk = chunk ?? fetchedChunk
  const markerChunkId = marker.kind === "chunk" ? marker.chunkId : undefined
  const markerChunkKey = markerChunkId
    ? `${marker.sourceId}:${markerChunkId}`
    : undefined
  const title = titleForEvidence(displayChunk, sourceMetadata, marker.sourceId)

  useEffect(() => {
    getAccessTokenRef.current = getAccessToken
  }, [getAccessToken])

  useEffect(() => {
    if (
      !open ||
      chunk ||
      fetchedChunk ||
      marker.kind !== "chunk" ||
      !markerChunkId ||
      !markerChunkKey ||
      requestedChunkKeyRef.current === markerChunkKey
    ) {
      return
    }

    let cancelled = false
    requestedChunkKeyRef.current = markerChunkKey
    setLoadState("loading")
    setLoadError(undefined)

    async function loadChunk() {
      try {
        const accessToken = await getAccessTokenRef.current()
        const response = await getBiblioChunkContext(
          {
            sourceId: marker.sourceId,
            chunkId: markerChunkId,
            before: 0,
            after: 0,
            maxChunks: 1,
            includeMetadata: true,
            maxContentCharsPerChunk: 12000,
          },
          accessToken
        )
        if (cancelled) return

        const hydratedChunk =
          response.chunks.find(
            responseChunk =>
              String(responseChunk.sourceId) === marker.sourceId &&
              responseChunk.chunkId === markerChunkId
          ) ?? response.chunks[0]

        if (hydratedChunk) {
          setFetchedChunk({
            sourceId: String(hydratedChunk.sourceId),
            chunkId: hydratedChunk.chunkId,
            page: hydratedChunk.page === undefined ? undefined : String(hydratedChunk.page),
            content: hydratedChunk.content,
            contentKind: hydratedChunk.contentKind,
            headings: hydratedChunk.headings,
            citation: hydratedChunk.citation,
            title: hydratedChunk.title,
            publicationYear: hydratedChunk.publicationYear,
            contentTruncated: hydratedChunk.contentTruncated,
          })
          setLoadState("loaded")
        } else {
          setLoadState("error")
          setLoadError("Chunk text was not found for this evidence marker.")
        }
      } catch (error) {
        if (cancelled) return
        setLoadState("error")
        setLoadError(error instanceof Error ? error.message : "Unable to load chunk text.")
      }
    }

    loadChunk()

    return () => {
      cancelled = true
    }
  }, [
    chunk,
    fetchedChunk,
    marker.kind,
    marker.sourceId,
    markerChunkId,
    markerChunkKey,
    open,
  ])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline text-foreground hover:text-primary underline underline-offset-2 "
          title={`Show evidence from ${markerDetail(marker)}`}
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-[min(36rem,calc(100vw-2rem))] max-h-[min(30rem,calc(100vh-7rem))] gap-0 overflow-hidden p-0"
      >
        <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
          <div className="min-w-0">
            <PopoverTitle className="truncate text-sm leading-5">{title}</PopoverTitle>
            <div className="mt-1 text-xs text-muted-foreground">{markerDetail(marker)}</div>
          </div>
          <div className="flex shrink-0 items-start gap-0.5">
            {/* Add this source to one of the user's collections. */}
            <AddSourceToCollectionButton sourceId={marker.sourceId} />
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              className="-mr-1 -mt-1"
              onClick={() => setOpen(false)}
              aria-label="Close evidence"
            >
              <X className="size-3.5" />
            </Button>
          </div>
        </div>

        <div className="max-h-[22rem] overflow-auto px-4 py-3">
          {displayChunk?.headings?.length ? (
            <div className="mb-2 text-xs font-medium text-muted-foreground">
              {displayChunk.headings.join(" / ")}
            </div>
          ) : null}

          {displayChunk?.content ? (
            <pre className="whitespace-pre-wrap break-words rounded-md border border-border bg-muted/30 p-3 font-sans text-xs leading-5 text-foreground">
              {displayChunk.content}
            </pre>
          ) : loadState === "loading" ? (
            <div className="rounded-md border border-border bg-muted/20 p-3 text-xs leading-5 text-muted-foreground">
              Loading chunk text...
            </div>
          ) : loadState === "error" ? (
            <div className="rounded-md border border-dashed border-border bg-muted/20 p-3 text-xs leading-5 text-muted-foreground">
              {loadError ?? "Unable to load chunk text."}
            </div>
          ) : (
            <div className="rounded-md border border-dashed border-border bg-muted/20 p-3 text-xs leading-5 text-muted-foreground">
              Chunk text is not available for this evidence marker.
            </div>
          )}

          {displayChunk?.contentTruncated ? (
            <div className="mt-2 text-xs text-muted-foreground">
              This chunk was truncated by the retrieval tool.
            </div>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  )
}
